import { useTranslation } from "react-i18next";
import Text from "src/components/Displays/Text";
import officina from "src/fonts/officina.woff2";
import { useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import PrimaryButton from "src/components/Shared/PrimaryButton";
import { useSessionStorage } from "src/hooks/useSessionStorage";
import { MicroFrontendDetails, SESSION_KEYS } from "src/consts/session";
import { Stack, Alert, Snackbar, SnackbarCloseReason } from "@mui/material";
import {
  authenticationUrl,
  customerAccessTransfer,
  siteAuthenticationMasterdata,
} from "src/api/siteAuthenticationClients";

const Start = () => {
  const [msg, setMsg] = useState("");
  const [open, setOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("translation");
  const transferId = searchParams.get("tid") || "";
  const [isLoading, setIsLoading] = useState(false);

  const [getTransferId, setTransferId] = useSessionStorage<string>(
    SESSION_KEYS.TRANSFER_ID,
  );
  const [getCustomerAccess, setCustomerAccess] = useSessionStorage<string>(
    SESSION_KEYS.MFE_CUSTOMER_ACCESS,
  );
  const [, setMicroFrontendDetails] = useSessionStorage<MicroFrontendDetails>(
    SESSION_KEYS.MICRO_FRONTEND_DETAILS,
  );

  const initialCall = useCallback(async () => {
    setIsLoading(true);
    try {
      const sessionTransferId = getTransferId();
      if (sessionTransferId) {
        await siteAuthenticationMasterdata({
          xIsonCustomerToken: getCustomerAccess() ?? "",
        });
        setIsLoading(false);
        return;
      }

      const customerAccessTransferResponse = await customerAccessTransfer({
        payload: {
          tid: transferId,
        },
      });
      setTransferId(transferId);
      setCustomerAccess(customerAccessTransferResponse.access);
      setMicroFrontendDetails(
        customerAccessTransferResponse.microFrontendDetails,
      );

      await siteAuthenticationMasterdata({
        xIsonCustomerToken: customerAccessTransferResponse.access,
      });

      setIsLoading(false);
    } catch (e) {
      const error = e as Error;
      setIsLoading(false);
      setMsg(error.message ?? "");
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initialCall();
  }, [initialCall]);

  const handleNextClick = async () => {
    setIsLoading(true);
    try {
      const authenticationUrlResponse = await authenticationUrl({
        xIsonCustomerToken: getCustomerAccess() ?? "",
      });

      window.location.href = authenticationUrlResponse.authenticationUrl;

      setTimeout(() => {
        setIsLoading(false);
      }, 2000);
    } catch (e) {
      const error = e as Error;
      setIsLoading(false);
      setMsg(error.msgId ?? error.message ?? "");
      setOpen(true);
    }
  };

  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setOpen(false);
  };

  return (
    <Stack gap={5}>
      <Text
        text={t("page.siteAuthenticationStart.pageTitle")}
        sx={(theme) => ({
          fontSize: "32px",
          fontWeight: "500",
          lineHeight: "40px",
          fontFamily: "officina",
          "@font-face": {
            fontFamily: "officina",
            src: `url(${officina}) format("woff2")`,
          },
          color: theme.palette.mainFont.primary,
        })}
      />

      <Text
        text={t("page.siteAuthenticationStart.pageSubTitle")}
        sx={(theme) => ({
          fontSize: "14px",
          lineHeight: "20px",
          fontWeight: "400",
          fontFamily: "Inter",
          color: theme.palette.mainFont.primary,
        })}
      />

      <PrimaryButton
        onClick={handleNextClick}
        text={t("page.siteAuthenticationStart.nextButton")}
        sx={(theme) => ({
          fontSize: "14px",
          lineHeight: "20px",
          maxWidth: { sm: "320px" },
          color: theme.palette.mainFont.primary,
        })}
        isLoading={isLoading}
      />

      <Snackbar open={open} autoHideDuration={3000} onClose={handleClose}>
        <Alert
          severity="error"
          variant="filled"
          onClose={handleClose}
          sx={{ width: "100%" }}
        >
          {msg}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default Start;
